/**
 * Topbar dropdown that gives every dashboard user a clear way back to the
 * public ConfirmPH website + (for owners) a one-click preview of their own
 * public listing.
 *
 * Owners see:
 *   - View my public page  -> /rentals/{slug or id}
 *   - Open ConfirmPH home  -> /
 *
 * Employees see:
 *   - Open ConfirmPH home  -> /
 *   - (no public-listing link: they may not have permission to publish)
 *
 * Also exposed as a sidebar-footer block + as items inside the mobile drawer
 * via `<BackToConfirmPHInline />` and `<BackToConfirmPHSidebar />`.
 */
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Globe, ChevronDown, Home } from "lucide-react";
import { api } from "../lib/api";
import useAccessiblePopover from "@/hooks/useAccessiblePopover";

function publicListingHref(company) {
  if (!company) return null;
  const slug = company.public_slug || company.slug;
  const cid = company.id;
  // Mini-site lives at /rentals/{slug or id}. The public route resolves
  // both: slug for friendly URLs, raw id as a fallback.
  return slug ? `/rentals/${slug}` : (cid ? `/rentals/${cid}` : null);
}

export default function BackToConfirmPHMenu({ user, company }) {
  const [open, setOpen] = useState(false);
  const [publicHref, setPublicHref] = useState(null);
  const { panelRef, triggerRef, closeAndRestore } = useAccessiblePopover(open, () => setOpen(false));

  const role = user?.account_type || user?.role;
  const isOwner = role === "business_owner" || role === "platform_admin";

  // Resolve public listing URL once. We prefer the path computed from the
  // company object on first render; if the slug isn't on the auth response
  // we fetch /api/public/business/{id} to discover whether the company is
  // actually published.
  useEffect(() => {
    const href = publicListingHref(company);
    setPublicHref(href);
    if (!href || !company?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/public/rentals/${company.id}`);
        if (!cancelled && data?.id) {
          const slug = data.public_slug || data.slug || data.id;
          setPublicHref(`/rentals/${slug}`);
        }
      } catch (_) { /* not publicly listed: keep fallback link */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.id]);

  return (
    <div className="relative" data-testid="back-to-confirmph">
      <button
        ref={triggerRef}
        id="back-to-confirmph-trigger"
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="back-to-confirmph-btn"
        className="hidden sm:inline-flex items-center gap-1 px-2 py-1.5 rounded-md hover:bg-slate-100 text-xs text-slate-600"
        title="ConfirmPH website navigation"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls="back-to-confirmph-popover"
      >
        <Globe className="w-3.5 h-3.5" /> ConfirmPH
        <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <>
          <button type="button" tabIndex={-1} className="fixed inset-0 z-30" onClick={closeAndRestore} aria-hidden="true" data-popover-backdrop />
          <div
            ref={panelRef}
            id="back-to-confirmph-popover"
            role="menu"
            aria-labelledby="back-to-confirmph-trigger"
            className="absolute right-0 mt-1.5 w-64 rounded-lg border border-slate-200 bg-white shadow-lg z-40 py-1"
            data-testid="back-to-confirmph-menu"
          >
            {isOwner && publicHref && (
              <a
                role="menuitem"
                href={publicHref}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                data-testid="biz-view-public-page"
                className="block px-3 py-2 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-900"
              >
                <div className="inline-flex items-center gap-1.5"><ExternalLink className="w-3.5 h-3.5" /> View my public page</div>
                <div className="text-[10px] text-slate-500 mt-0.5">Opens your listing in a new tab</div>
              </a>
            )}
            <Link
              role="menuitem"
              to="/"
              data-testid="back-to-confirmph-home"
              onClick={() => setOpen(false)}
              className="block px-3 py-2 text-sm text-slate-700 hover:bg-slate-100"
            >
              <div className="inline-flex items-center gap-1.5"><Home className="w-3.5 h-3.5" /> Back to ConfirmPH</div>
              <div className="text-[10px] text-slate-500 mt-0.5">Open the public ConfirmPH home</div>
            </Link>
            <Link
              role="menuitem"
              to="/rentals"
              data-testid="back-to-confirmph-rentals"
              onClick={() => setOpen(false)}
              className="block px-3 py-2 text-sm text-slate-700 hover:bg-slate-100"
            >
              <div className="inline-flex items-center gap-1.5"><ExternalLink className="w-3.5 h-3.5" /> Discover rentals directory</div>
            </Link>
          </div>
        </>
      )}
    </div>
  );
}

/** Sidebar-footer + mobile-drawer compatible inline block. */
export function BackToConfirmPHSidebar({ user, company }) {
  const role = user?.account_type || user?.role;
  const isOwner = role === "business_owner" || role === "platform_admin";
  const href = publicListingHref(company);
  return (
    <div className="mt-4 pt-4 border-t border-slate-200 space-y-1" data-testid="back-to-confirmph-sidebar">
      {isOwner && href && (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="biz-view-public-page-sidebar"
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-emerald-800 bg-emerald-50/60 hover:bg-emerald-100"
        >
          <ExternalLink className="w-3.5 h-3.5" />
          <span className="flex-1">View my public page</span>
        </a>
      )}
      <Link
        to="/"
        data-testid="back-to-confirmph-sidebar-home"
        className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-100"
      >
        <Globe className="w-3.5 h-3.5" />
        <span className="flex-1">Back to ConfirmPH</span>
      </Link>
    </div>
  );
}
